import styled, { createGlobalStyle, keyframes } from "styled-components";
import { FONTS, responsive } from "Styles/Constants";
import { IMAGES } from "Helpers";

// The dashboard can still scroll on short screens, but the browser's (white) scrollbar is never drawn.
// Rendered only while the dashboard is mounted, so other pages keep their scrollbars.
export const HideBrowserScrollbars = createGlobalStyle`
	html,
	body {
		scrollbar-width: none;
		-ms-overflow-style: none;
	}
	html::-webkit-scrollbar,
	body::-webkit-scrollbar {
		display: none;
		width: 0;
		height: 0;
	}
`;

// Subject illustration: rises in when a subject is picked
const iconIn = keyframes`
	0% { opacity: 0; transform: translateY(28px) scale(0.94); }
	100% { opacity: 1; transform: translateY(0) scale(1); }
`;

export const DashboardWrapper = styled.div`
	position: relative;
	min-height: 100vh;
	overflow: hidden;
	display: flex;
	flex-direction: column;
	color: #fff;
	clip-path: ${(props) => (props.loading ? "inset(0)" : " ")};
	transition: ${(props) => (props.loading ? "" : "filter 0.5s linear")};

	.background-image {
		width: 100%;
		z-index: -1;
		height: 100%;
		overflow: hidden;
		position: absolute;
		top: 0;
		left: 0;

		/* black tint over the subject background, darker behind the text on the left (not on the default welcome background) */
		&::after {
			content: "";
			position: absolute;
			inset: 0;
			z-index: 3;
			pointer-events: none;
			background: ${(props) =>
				props?.bgImg?.backgroundImg
					? "linear-gradient(90deg, rgba(8, 8, 14, 0.82) 0%, rgba(8, 8, 14, 0.5) 55%, rgba(8, 8, 14, 0.3) 100%)"
					: "transparent"};
		}
	}

	.audio {
		display: none;
	}

	/* ---------- Header ---------- */
	.header-container {
		background-color: ${(props) => (props?.bgImg?.backgroundImg ? "rgba(0, 0, 0, 0.35)" : "rgba(255, 255, 255, 0.9)")};
		padding: 1%;
		.inner-header {
			display: flex;
			width: 90%;
			margin: 0px auto;
			justify-content: space-between;
			align-items: center;

			.logo-img {
				background-image: ${(props) => (props?.bgImg?.backgroundImg ? `url(${IMAGES.DASHBOARD.SUBJECT_MASTER_LOGO_WHITE})` : `url(${IMAGES.DASHBOARD.SUBJECT_MASTER_LOGO_BLACK})`)};
				width: 110px;
				height: 40px;
				background-repeat: no-repeat;
				background-size: contain;
			}

			.profile-container {
				display: flex;
				align-items: center;
				.profile-picture {
					width: 40px;
					height: 40px;
					object-fit: cover;
					border-radius: 50%;
					margin-left: 10px;
					flex-shrink: 0;
					display: block;
				}
				.text {
					color: ${(props) => (props?.bgImg?.backgroundImg ? `#fff` : `#000`)};
					font-size: 20px;
					width: 200px;
					text-overflow: ellipsis;
					white-space: nowrap;
					overflow: hidden;
					text-align: end;
				}
				.arrow {
					background-image: ${(props) => (props?.bgImg?.backgroundImg ? `url(${IMAGES.COMMONS.WHITE_ARROW})` : `url(${IMAGES.COMMONS.BLACK_ARROW})`)};
					width: 15px;
					height: 15px;
					background-repeat: no-repeat;
					background-size: cover;
					background-position: center;
					rotate: 90deg;
					margin-left: 10px;
					cursor: pointer;
				}
			}
		}
	}

	.progress {
		display: flex;
		justify-content: center;
		align-items: center;
		height: 350px;
		width: 100%;
	}

	/* ---------- Hero: selected subject ---------- */
	.hero {
		flex-grow: 1;
		width: 90%;
		margin: 0 auto;
		padding: 2% 0 0;
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 40px;

		.hero-text {
			flex: 1 1 480px;
			min-width: 0;
			display: flex;
			flex-direction: column;
			gap: 18px;
		}

		.eyebrow {
			font-family: ${FONTS.TERTIARY_BOLD};
			font-size: 14px;
			letter-spacing: 3px;
			text-transform: uppercase;
			color: ${(props) => props?.bgImg?.accent || "#fff"};
		}

		.subject-name {
			margin: 0;
			font-family: ${FONTS.TERTIARY_BLACK};
			font-weight: normal;
			font-size: 84px;
			line-height: 0.95;
			letter-spacing: 1px;
			text-transform: uppercase;
			color: #fff;
			${responsive.LAPTOP`font-size: 64px;`}
			${responsive.TABLET`font-size: 48px;`}
		}

		/* quote, author line and Play button keep the website's original text styles */
		.quote {
			margin: 0;
			max-width: 760px;
			font-family: ${FONTS.TERTIARY_BOLD};
			font-size: 34px;
			letter-spacing: 2px;
			word-spacing: 4px;
			color: #fff;
			text-shadow: 0 2px 8px rgba(0, 0, 0, 0.6);
		}

		.quote-by {
			margin-top: 5px;
			font-family: ${FONTS.TERTIARY_BOLD};
			font-size: 28px;
			letter-spacing: 2px;
			word-spacing: 4px;
			color: #fff;
		}

		.play {
			color: #fff;
			font-family: ${FONTS.TERTIARY_SEMI_BOLD};
			/* same look, but grows with the screen like the subject cards (sizes match today's up to ~1750px wide) */
			padding: clamp(10px, 0.6vw, 16px) clamp(20px, 1.2vw, 32px);
			/* same corner radius as the subject card images below (12.5% of the card width) */
			border-radius: calc(0.125 * clamp(130px, 9.5vw, 260px));
			width: clamp(136px, 8.57vw, 230px);
			min-width: clamp(125px, 8.57vw, 230px);
			min-height: clamp(45px, 2.86vw, 72px);
			font-size: clamp(15px, 1.03vw, 24px);
			text-align: center;
			background-color: black;
			margin-top: 5%;
			cursor: pointer;

			.MuiButton-label {
				font-size: clamp(15px, 1.03vw, 24px);
			}

			&::after {
				border-radius: calc(0.125 * clamp(130px, 9.5vw, 260px));
			}
			/* the glow layer sits 2px outside the button, so its corner is 2px larger to stay parallel */
			&::before {
				border-radius: calc(0.125 * clamp(130px, 9.5vw, 260px) + 2px);
			}
		}

		.hero-art {
			position: relative;
			flex: 0 1 460px;
			display: flex;
			justify-content: center;
			align-items: center;

			.hero-art-inner {
				position: relative;
				width: 100%;
				max-width: 440px;
				height: clamp(200px, 36vh, 360px);
				animation: ${iconIn} 0.7s ease-out both;

				img {
					position: absolute;
					top: 0;
					left: 0;
					width: 100%;
					height: 100%;
					object-fit: contain;
				}
			}

			.icon {
				filter: drop-shadow(0 24px 40px rgba(0, 0, 0, 0.55));
			}

			/* soft, pulsing glow in the illustration's own colours (a blurred, brightened copy of it) */
			.icon-glow {
				filter: blur(26px) saturate(2) brightness(1.45);
				pointer-events: none;
				opacity: 0.85;
				transform: scale(1.18);
			}

			@media (prefers-reduced-motion: reduce) {
				.hero-art-inner {
					animation: none;
				}
			}

			${responsive.TABLET`display: none;`}
		}
	}

	/* ---------- Welcome (no subject selected yet) ---------- */
	.welcome-board-text {
		font-size: 32px;
		font-weight: 600;
		display: flex;
		margin: 60px 0;
		color: #000;
	}
	.welcome-container {
		flex-grow: 1;
		margin: 50px auto;

		${responsive.LAPTOP`
			margin: 25px auto;
		`}
	}

	/* ---------- Subject shelf ---------- */
	.subject-container {
		width: 92%;
		margin: 0 auto;
		padding: 12px 0 32px;

		.subject-shelf {
			position: relative;
			padding: 0 clamp(64px, 3.6vw, 84px);
			border-radius: 28px;
			background-color: rgba(10, 10, 18, 0.45);
			border: 1px solid rgba(255, 255, 255, 0.12);
			backdrop-filter: blur(12px);

			${responsive.TABLET`padding: 0 52px;`}
		}

		/* room above and below the cards so the lifted, glowing selected card is not cut off */
		.slick-list {
			padding: 44px 0 24px;
		}

		.prev-arrow,
		.next-arrow {
			position: absolute;
			top: 50%;
			transform: translateY(-50%);
			z-index: 2;
			width: clamp(48px, 2.6vw, 64px);
			height: clamp(48px, 2.6vw, 64px);
			padding: 0;
			color: #fff;
			border: 1px solid rgba(255, 255, 255, 0.3);
			background-color: rgba(255, 255, 255, 0.08);

			&:hover {
				background-color: rgba(255, 255, 255, 0.18);
			}

			&.slick-disabled {
				opacity: 0.35;
			}

			${responsive.TABLET`
				width: 40px;
				height: 40px;
			`}
		}
		.prev-arrow {
			left: calc(-1 * clamp(56px, 3vw, 72px));
			${responsive.TABLET`left: -46px;`}
		}
		.next-arrow {
			right: calc(-1 * clamp(56px, 3vw, 72px));
			${responsive.TABLET`right: -46px;`}
		}

		/* cards grow with the screen: 9.5vw keeps room for the selected card's glow inside each slide */
		.subject-img {
			display: block;
			width: clamp(130px, 9.5vw, 260px);
			height: auto;
			aspect-ratio: 560 / 782;
			margin: 0 auto;
			padding: 0;
			border: 0;
			/* matches the rounded corners baked into the card image at any size (12.5% of width, 8.95% of height) */
			border-radius: 12.5% / 8.95%;
			cursor: pointer;
			background-color: transparent;
			background-repeat: no-repeat;
			background-size: cover;
			background-position: center;
			opacity: 0.8;
			box-shadow: 0 10px 20px rgba(0, 0, 0, 0.35);
			transition: transform 0.35s ease, box-shadow 0.35s ease, opacity 0.35s ease;

			&:hover {
				opacity: 1;
				transform: translateY(-6px);
			}

			${responsive.PHABLET`
				width: 130px;
			`}
		}

		.selected-subject,
		.selected-subject:hover {
			opacity: 1;
			transform: translateY(-12px) scale(1.06);
			box-shadow: 0 0 0 3px var(--accent), 0 0 18px 4px rgba(var(--accent-rgb), 0.65), 0 18px 30px rgba(0, 0, 0, 0.5);
		}
	}
`;
